import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { 
  Plus, Edit, Trash2, Image, Eye, MousePointer, Send,
  Calendar, Clock, Upload, BarChart3, ExternalLink,
  Megaphone, TrendingUp, Target, Navigation, Settings, MonitorSmartphone,
  Palette, Type, Square, RotateCcw, Save, RefreshCw,
  Layout, Sparkles, Move
} from "lucide-react";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "");

export default function PublicidadesPage() {
  const [activeTab, setActiveTab] = useState("publicidades");
  const [publicidades, setPublicidades] = useState([]);
  const [estadisticas, setEstadisticas] = useState([]);
  const [menusDisponibles, setMenusDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDraggingImagen, setIsDraggingImagen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // Configuración visual
  const [configVisual, setConfigVisual] = useState({
    // Colores del modal
    color_fondo_modal: "#ffffff",
    color_overlay: "#000000",
    opacidad_overlay: 50,
    // Imagen
    imagen_ajuste: "cover",
    imagen_altura: "200",
    imagen_redondeo: "0",
    mostrar_degradado_imagen: true,
    color_degradado_inicio: "transparent",
    color_degradado_fin: "rgba(0,0,0,0.3)",
    // Título
    color_titulo: "#1f2937",
    tamaño_titulo: "2xl",
    fuente_titulo: "sans",
    peso_titulo: "bold",
    // Descripción
    color_descripcion: "#6b7280",
    tamaño_descripcion: "base",
    // Botón principal
    color_boton_fondo: "#3b82f6",
    color_boton_texto: "#ffffff",
    color_boton_hover: "#2563eb",
    boton_redondeo: "md",
    boton_tamaño: "md",
    // Botón cerrar
    color_boton_cerrar: "transparent",
    color_boton_cerrar_texto: "#6b7280",
    estilo_boton_cerrar: "ghost",
    // Modal
    modal_ancho: "lg",
    modal_redondeo: "xl",
    modal_sombra: "2xl",
    // Espaciado
    padding_contenido: "24",
    espacio_imagen_contenido: "0",
    espacio_titulo_descripcion: "8",
    espacio_descripcion_botones: "16",
    espacio_entre_botones: "12",
    // Animación
    animacion_entrada: "fade",
    duracion_animacion: 200
  });
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [usuariosLista, setUsuariosLista] = useState([]);
  // Diálogo "Enviar Prueba" de notificación de escritorio
  const [pruebaPush, setPruebaPush] = useState({ open: false, pub: null, selectedUserId: '', sending: false });
  const [pruebaSearch, setPruebaSearch] = useState('');

  const diasSemanaOptions = [
    { value: 0, label: 'Lun' },
    { value: 1, label: 'Mar' },
    { value: 2, label: 'Mié' },
    { value: 3, label: 'Jue' },
    { value: 4, label: 'Vie' },
    { value: 5, label: 'Sáb' },
    { value: 6, label: 'Dom' },
  ];

  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    imagen_url: '',
    // Botones (hasta 3)
    botones: [
      { texto: '', url: '', activo: true }
    ],
    // Legacy: mantener compatibilidad con el primer botón
    boton_texto: '',
    boton_url: '',
    fecha_inicio: '',
    fecha_fin: '',
    hora_inicio: '',
    hora_fin: '',
    frecuencia: 'una_vez_sesion',
    recurrencia_valor: '',
    recurrencia_unidad: 'horas',
    activo: true,
    orden: 0,
    menu_destino: '',
    submenu_destino: '',
    // Notificación de escritorio (Web Push)
    notificacion_escritorio: false,
    notificacion_target_user_id: '',
    dias_semana: []
  });

  // Función para agregar un nuevo botón (máximo 3)
  const agregarBoton = () => {
    if (formData.botones.length < 3) {
      setFormData({
        ...formData,
        botones: [...formData.botones, { texto: '', url: '', activo: true }]
      });
    }
  };

  // Función para eliminar un botón
  const eliminarBoton = (index) => {
    if (formData.botones.length > 1) {
      const nuevosBotones = formData.botones.filter((_, i) => i !== index);
      setFormData({ ...formData, botones: nuevosBotones });
    }
  };

  // Función para actualizar un botón específico
  const actualizarBoton = (index, campo, valor) => {
    const nuevosBotones = [...formData.botones];
    nuevosBotones[index] = { ...nuevosBotones[index], [campo]: valor };
    setFormData({ ...formData, botones: nuevosBotones });
  };

  const frecuenciaOptions = [
    { value: 'siempre', label: 'Siempre que entre al dashboard' },
    { value: 'una_vez_sesion', label: 'Una vez por sesión' },
    { value: 'una_vez_dia', label: 'Una vez al día' },
    { value: 'una_vez_total', label: 'Solo una vez (nunca más)' },
    { value: 'recurrente', label: 'Recurrente (personalizado)' }
  ];

  const recurrenciaUnidades = [
    { value: 'minutos', label: 'Minutos' },
    { value: 'horas', label: 'Horas' },
    { value: 'dias', label: 'Días' },
    { value: 'semanas', label: 'Semanas' }
  ];

  const cargarPublicidades = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const [pubRes, statsRes, menusRes] = await Promise.all([
        axios.get(`${API_URL}/api/publicidades`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/publicidades/estadisticas`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/publicidades/menus-disponibles`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setPublicidades(pubRes.data);
      setEstadisticas(statsRes.data);
      setMenusDisponibles(menusRes.data || []);
      // Cargar lista de usuarios para el selector de destinatario de pruebas
      try {
        const usersRes = await axios.get(`${API_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsuariosLista(usersRes.data || []);
      } catch (e) {
        // no bloquear si falla
      }
    } catch (error) {
      toast.error("Error al cargar publicidades");
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarConfiguracion = useCallback(async () => {
    try {
      setLoadingConfig(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/publicidades/configuracion-visual`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigVisual(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Error al cargar configuración:", error);
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  const guardarConfiguracion = async () => {
    try {
      setLoadingConfig(true);
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/api/publicidades/configuracion-visual`, configVisual, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setLoadingConfig(false);
    }
  };

  const resetearConfiguracion = async () => {
    if (!window.confirm("¿Resetear a los valores por defecto?")) return;
    try {
      setLoadingConfig(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/api/publicidades/configuracion-visual/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigVisual(prev => ({ ...prev, ...response.data }));
      toast.success("Configuración reseteada");
    } catch (error) {
      toast.error("Error al resetear");
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleConfigChange = (field, value) => {
    setConfigVisual(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    cargarPublicidades();
    cargarConfiguracion();
  }, [cargarPublicidades, cargarConfiguracion]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    await subirArchivoImagen(file);
  };

  const subirArchivoImagen = async (file) => {
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      toast.error("El archivo debe ser una imagen (JPG, PNG, GIF o WebP)");
      return;
    }
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/publicidades/upload-imagen`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      handleInputChange('imagen_url', response.data.imagen_url);
      toast.success("Imagen subida correctamente");
    } catch (error) {
      toast.error("Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  const handleDropImagen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImagen(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) subirArchivoImagen(file);
  };

  const handleDragOverImagen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDraggingImagen) setIsDraggingImagen(true);
  };

  const handleDragLeaveImagen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImagen(false);
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Preparar botones: filtrar solo los que tienen texto
      const botonesValidos = formData.botones.filter(b => b.texto && b.texto.trim());
      
      // Preparar datos para envío, convirtiendo strings vacíos a null para campos opcionales
      const dataToSend = {
        ...formData,
        recurrencia_valor: formData.recurrencia_valor === '' || formData.recurrencia_valor === null 
          ? null 
          : parseInt(formData.recurrencia_valor) || null,
        orden: parseInt(formData.orden) || 0,
        fecha_inicio: formData.fecha_inicio || null,
        fecha_fin: formData.fecha_fin || null,
        hora_inicio: formData.hora_inicio || null,
        hora_fin: formData.hora_fin || null,
        imagen_url: formData.imagen_url || null,
        // Primer botón para compatibilidad
        boton_texto: botonesValidos[0]?.texto || null,
        boton_url: botonesValidos[0]?.url || null,
        // Array de todos los botones
        botones: botonesValidos.length > 0 ? botonesValidos : null,
        titulo: formData.titulo || null,
        descripcion: formData.descripcion || null,
        menu_destino: formData.menu_destino || null,
        submenu_destino: formData.submenu_destino || null,
        // Notificación de escritorio (Web Push)
        notificacion_escritorio: !!formData.notificacion_escritorio,
        // "" = todos (se envía string vacío para poder volver a "Todos")
        notificacion_target_user_id: formData.notificacion_target_user_id || "",
        dias_semana: Array.isArray(formData.dias_semana) ? formData.dias_semana : [],
      };
      
      if (editando) {
        await axios.put(
          `${API_URL}/api/publicidades/${editando}`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Publicidad actualizada");
      } else {
        await axios.post(
          `${API_URL}/api/publicidades`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Publicidad creada");
      }

      setDialogOpen(false);
      resetForm();
      cargarPublicidades();
    } catch (error) {
      console.error("Error al guardar:", error.response?.data || error);
      toast.error("Error al guardar la publicidad");
    }
  };

  const handleEditar = (pub) => {
    setEditando(pub.id);
    // Cargar botones existentes o crear uno a partir de boton_texto/boton_url
    let botones = pub.botones || [];
    if (botones.length === 0 && (pub.boton_texto || pub.boton_url)) {
      botones = [{ texto: pub.boton_texto || '', url: pub.boton_url || '', activo: true }];
    }
    if (botones.length === 0) {
      botones = [{ texto: '', url: '', activo: true }];
    }
    
    setFormData({
      titulo: pub.titulo || '',
      descripcion: pub.descripcion || '',
      imagen_url: pub.imagen_url || '',
      botones: botones,
      boton_texto: pub.boton_texto || '',
      boton_url: pub.boton_url || '',
      fecha_inicio: pub.fecha_inicio ? pub.fecha_inicio.split('T')[0] : '',
      fecha_fin: pub.fecha_fin ? pub.fecha_fin.split('T')[0] : '',
      hora_inicio: pub.hora_inicio || '',
      hora_fin: pub.hora_fin || '',
      frecuencia: pub.frecuencia || 'una_vez_sesion',
      recurrencia_valor: pub.recurrencia_valor || '',
      recurrencia_unidad: pub.recurrencia_unidad || 'horas',
      activo: pub.activo ?? true,
      orden: pub.orden || 0,
      menu_destino: pub.menu_destino || '',
      submenu_destino: pub.submenu_destino || '',
      notificacion_escritorio: pub.notificacion_escritorio ?? false,
      notificacion_target_user_id: pub.notificacion_target_user_id || '',
      dias_semana: Array.isArray(pub.dias_semana) ? pub.dias_semana : []
    });
    setDialogOpen(true);
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta publicidad?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/publicidades/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Publicidad eliminada");
      cargarPublicidades();
    } catch (error) {
      toast.error("Error al eliminar la publicidad");
    }
  };

  const handleToggleActivo = async (pub) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/publicidades/${pub.id}`,
        { activo: !pub.activo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(pub.activo ? "Publicidad desactivada" : "Publicidad activada");
      cargarPublicidades();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const handlePreview = (pub) => {
    setPreviewData(pub);
    setPreviewOpen(true);
  };

  const abrirPruebaPush = (pub) => {
    setPruebaSearch('');
    setPruebaPush({ open: true, pub, selectedUserId: '', sending: false });
  };

  const enviarPruebaPush = async () => {
    if (!pruebaPush.pub || !pruebaPush.selectedUserId) {
      toast.error("Selecciona un usuario para enviar la prueba");
      return;
    }
    setPruebaPush(prev => ({ ...prev, sending: true }));
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/api/publicidades/${pruebaPush.pub.id}/enviar-prueba`,
        { user_id: pruebaPush.selectedUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success(res.data.message || "Prueba enviada");
        setPruebaPush({ open: false, pub: null, selectedUserId: '', sending: false });
      } else {
        toast.warning(res.data.message || "No se pudo enviar la prueba", { duration: 7000 });
        setPruebaPush(prev => ({ ...prev, sending: false }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar la prueba");
      setPruebaPush(prev => ({ ...prev, sending: false }));
    }
  };

  const resetForm = () => {
    setEditando(null);
    setFormData({
      titulo: '',
      descripcion: '',
      imagen_url: '',
      botones: [{ texto: '', url: '', activo: true }],
      boton_texto: '',
      boton_url: '',
      fecha_inicio: '',
      fecha_fin: '',
      hora_inicio: '',
      hora_fin: '',
      frecuencia: 'una_vez_sesion',
      recurrencia_valor: '',
      recurrencia_unidad: 'horas',
      activo: true,
      orden: 0,
      menu_destino: '',
      submenu_destino: '',
      notificacion_escritorio: false,
      notificacion_target_user_id: '',
      dias_semana: []
    });
  };

  // Obtener submenús del menú seleccionado
  const getSubmenusDelMenu = () => {
    if (!formData.menu_destino) return [];
    const menu = menusDisponibles.find(m => m.id === formData.menu_destino);
    return menu?.submenus || [];
  };

  // Obtener nombre del menú por ID
  const getMenuLabel = (menuId) => {
    const menu = menusDisponibles.find(m => m.id === menuId);
    return menu?.label || menuId;
  };

  // Obtener nombre del submenú por ID
  const getSubmenuLabel = (menuId, submenuId) => {
    const menu = menusDisponibles.find(m => m.id === menuId);
    const submenu = menu?.submenus?.find(s => s.id === submenuId);
    return submenu?.label || submenuId;
  };

  const getFrecuenciaLabel = (value, pub = null) => {
    if (value === 'recurrente' && pub?.recurrencia_valor && pub?.recurrencia_unidad) {
      const unidadLabel = recurrenciaUnidades.find(u => u.value === pub.recurrencia_unidad)?.label || pub.recurrencia_unidad;
      return `Cada ${pub.recurrencia_valor} ${unidadLabel.toLowerCase()}`;
    }
    const option = frecuenciaOptions.find(o => o.value === value);
    return option ? option.label : value;
  };

  const getEstadistica = (id) => {
    return estadisticas.find(s => s.id === id) || { total_vistas: 0, total_clicks: 0, ctr: 0 };
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-6 pt-14 md:pt-6 lg:p-8 overflow-auto" data-testid="publicidades-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-3">
                <Megaphone className="h-8 w-8 text-blue-600" />
                Publicidades Emergentes
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Gestiona las promociones y anuncios que aparecen al iniciar sesión
              </p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="publicidades" className="gap-2">
                <Megaphone className="h-4 w-4" />
                Publicidades
              </TabsTrigger>
              <TabsTrigger value="configuracion" className="gap-2">
                <Settings className="h-4 w-4" />
                Configuración
              </TabsTrigger>
            </TabsList>

            {/* TAB: PUBLICIDADES */}
            <TabsContent value="publicidades" className="space-y-6">
              <div className="flex justify-end">
                <Button
                  onClick={() => { resetForm(); setDialogOpen(true); }}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  data-testid="nueva-publicidad-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Publicidad
                </Button>
              </div>

              {/* Estadísticas globales */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 dark:text-blue-400">Total Publicidades</p>
                    <p className="text-2xl font-bold text-blue-800 dark:text-blue-200">{publicidades.length}</p>
                  </div>
                  <Megaphone className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 dark:text-green-400">Activas</p>
                    <p className="text-2xl font-bold text-green-800 dark:text-green-200">
                      {publicidades.filter(p => p.activo).length}
                    </p>
                  </div>
                  <Eye className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 dark:text-purple-400">Total Vistas</p>
                    <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">
                      {estadisticas.reduce((sum, s) => sum + s.total_vistas, 0)}
                    </p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 dark:text-orange-400">Total Clicks</p>
                    <p className="text-2xl font-bold text-orange-800 dark:text-orange-200">
                      {estadisticas.reduce((sum, s) => sum + s.total_clicks, 0)}
                    </p>
                  </div>
                  <MousePointer className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabla de publicidades */}
          <Card>
            <CardHeader>
              <CardTitle>Lista de Publicidades</CardTitle>
              <CardDescription>
                Administra todas tus publicidades y promociones
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : publicidades.length === 0 ? (
                <div className="text-center py-12">
                  <Megaphone className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No hay publicidades creadas</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Crea tu primera publicidad para promocionar contenido
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Imagen</TableHead>
                      <TableHead>Título</TableHead>
                      <TableHead>Destino</TableHead>
                      <TableHead>Frecuencia</TableHead>
                      <TableHead>Período</TableHead>
                      <TableHead>Estadísticas</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {publicidades.map((pub) => {
                      const stats = getEstadistica(pub.id);
                      return (
                        <TableRow key={pub.id}>
                          <TableCell>
                            {pub.imagen_url ? (
                              <img
                                src={pub.imagen_url.startsWith('http') ? pub.imagen_url : `${API_URL}${pub.imagen_url}`}
                                alt={pub.titulo || "Publicidad"}
                                className="w-16 h-12 object-cover rounded-md"
                              />
                            ) : (
                              <div className="w-16 h-12 bg-slate-200 rounded-md flex items-center justify-center">
                                <Image className="h-6 w-6 text-slate-400" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{pub.titulo || "Sin título"}</p>
                              {pub.descripcion && (
                                <p className="text-sm text-slate-500 truncate max-w-xs">
                                  {pub.descripcion}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {pub.menu_destino ? (
                                <div className="flex items-center gap-1">
                                  <Target className="h-3 w-3 text-blue-500" />
                                  <span className="text-blue-600 font-medium">
                                    {getMenuLabel(pub.menu_destino)}
                                  </span>
                                  {pub.submenu_destino && (
                                    <>
                                      <Navigation className="h-3 w-3 text-slate-400" />
                                      <span className="text-slate-600">
                                        {getSubmenuLabel(pub.menu_destino, pub.submenu_destino)}
                                      </span>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-400 flex items-center gap-1">
                                  <Target className="h-3 w-3" />
                                  Todas las páginas
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600">
                              {getFrecuenciaLabel(pub.frecuencia, pub)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {pub.fecha_inicio && (
                                <div className="flex items-center gap-1 text-slate-600">
                                  <Calendar className="h-3 w-3" />
                                  {new Date(pub.fecha_inicio).toLocaleDateString()}
                                </div>
                              )}
                              {pub.fecha_fin && (
                                <div className="flex items-center gap-1 text-slate-500">
                                  <span>hasta</span>
                                  {new Date(pub.fecha_fin).toLocaleDateString()}
                                </div>
                              )}
                              {!pub.fecha_inicio && !pub.fecha_fin && (
                                <span className="text-slate-400">Sin límite</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-sm">
                                <Eye className="h-4 w-4 text-blue-500" />
                                <span>{stats.total_vistas} vistas</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <MousePointer className="h-4 w-4 text-green-500" />
                                <span>{stats.total_clicks} clicks</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <TrendingUp className="h-4 w-4 text-purple-500" />
                                <span>{stats.ctr}% CTR</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={pub.activo}
                                onCheckedChange={() => handleToggleActivo(pub)}
                                data-testid={`publicidad-switch-${pub.id}`}
                              />
                              <Badge variant={pub.activo ? "default" : "secondary"}>
                                {pub.activo ? "Activa" : "Inactiva"}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {pub.notificacion_escritorio && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => abrirPruebaPush(pub)}
                                  title="Enviar prueba de notificación de escritorio"
                                  data-testid={`enviar-prueba-btn-${pub.id}`}
                                  className="text-indigo-500 hover:text-indigo-700"
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handlePreview(pub)}
                                title="Vista previa"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditar(pub)}
                                title="Editar"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEliminar(pub.id)}
                                className="text-red-500 hover:text-red-700"
                                title="Eliminar"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
            </TabsContent>

            {/* TAB: CONFIGURACIÓN VISUAL */}
            <TabsContent value="configuracion" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Palette className="h-5 w-5" />
                        Configuración Visual
                      </CardTitle>
                      <CardDescription>
                        Personaliza la apariencia de las publicidades emergentes
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={resetearConfiguracion} disabled={loadingConfig}>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Resetear
                      </Button>
                      <Button onClick={guardarConfiguracion} disabled={loadingConfig}>
                        {loadingConfig ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Guardar Cambios
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Columna izquierda: Configuraciones */}
                    <ScrollArea className="h-[600px] pr-4">
                      <div className="space-y-6">
                        {/* Sección: Modal */}
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2 border-b pb-2">
                            <Layout className="h-4 w-4" />
                            Modal
                          </h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Ancho del modal</Label>
                              <Select value={configVisual.modal_ancho} onValueChange={(v) => handleConfigChange('modal_ancho', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="sm">Pequeño (384px)</SelectItem>
                                  <SelectItem value="md">Mediano (448px)</SelectItem>
                                  <SelectItem value="lg">Grande (512px)</SelectItem>
                                  <SelectItem value="xl">Extra Grande (576px)</SelectItem>
                                  <SelectItem value="2xl">2XL (672px)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Redondeo del modal</Label>
                              <Select value={configVisual.modal_redondeo} onValueChange={(v) => handleConfigChange('modal_redondeo', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Sin redondeo</SelectItem>
                                  <SelectItem value="sm">Pequeño</SelectItem>
                                  <SelectItem value="md">Mediano</SelectItem>
                                  <SelectItem value="lg">Grande</SelectItem>
                                  <SelectItem value="xl">Extra Grande</SelectItem>
                                  <SelectItem value="2xl">2XL</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Sombra</Label>
                              <Select value={configVisual.modal_sombra} onValueChange={(v) => handleConfigChange('modal_sombra', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Sin sombra</SelectItem>
                                  <SelectItem value="sm">Pequeña</SelectItem>
                                  <SelectItem value="md">Mediana</SelectItem>
                                  <SelectItem value="lg">Grande</SelectItem>
                                  <SelectItem value="xl">Extra Grande</SelectItem>
                                  <SelectItem value="2xl">2XL</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Color de fondo</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="color"
                                  value={configVisual.color_fondo_modal}
                                  onChange={(e) => handleConfigChange('color_fondo_modal', e.target.value)}
                                  className="w-12 h-10 p-1 cursor-pointer"
                                />
                                <Input
                                  value={configVisual.color_fondo_modal}
                                  onChange={(e) => handleConfigChange('color_fondo_modal', e.target.value)}
                                  className="flex-1"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Opacidad del overlay ({configVisual.opacidad_overlay}%)</Label>
                            <Slider
                              value={[configVisual.opacidad_overlay]}
                              onValueChange={([v]) => handleConfigChange('opacidad_overlay', v)}
                              min={0}
                              max={100}
                              step={5}
                            />
                          </div>
                        </div>

                        {/* Sección: Imagen */}
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2 border-b pb-2">
                            <Image className="h-4 w-4" />
                            Imagen
                          </h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Ajuste de imagen</Label>
                              <Select value={configVisual.imagen_ajuste} onValueChange={(v) => handleConfigChange('imagen_ajuste', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="cover">Cubrir</SelectItem>
                                  <SelectItem value="contain">Contener</SelectItem>
                                  <SelectItem value="fill">Llenar</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Altura (px)</Label>
                              <Input
                                type="number"
                                value={configVisual.imagen_altura}
                                onChange={(e) => handleConfigChange('imagen_altura', e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Redondeo (px)</Label>
                              <Input
                                type="number"
                                value={configVisual.imagen_redondeo}
                                onChange={(e) => handleConfigChange('imagen_redondeo', e.target.value)}
                              />
                            </div>
                            <div className="flex items-center gap-2 pt-6">
                              <Switch
                                checked={configVisual.mostrar_degradado_imagen}
                                onCheckedChange={(v) => handleConfigChange('mostrar_degradado_imagen', v)}
                              />
                              <Label>Mostrar degradado</Label>
                            </div>
                          </div>
                        </div>

                        {/* Sección: Título */}
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2 border-b pb-2">
                            <Type className="h-4 w-4" />
                            Título
                          </h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Color del título</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="color"
                                  value={configVisual.color_titulo}
                                  onChange={(e) => handleConfigChange('color_titulo', e.target.value)}
                                  className="w-12 h-10 p-1 cursor-pointer"
                                />
                                <Input
                                  value={configVisual.color_titulo}
                                  onChange={(e) => handleConfigChange('color_titulo', e.target.value)}
                                  className="flex-1"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Tamaño</Label>
                              <Select value={configVisual.tamaño_titulo} onValueChange={(v) => handleConfigChange('tamaño_titulo', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="base">Normal</SelectItem>
                                  <SelectItem value="lg">Grande</SelectItem>
                                  <SelectItem value="xl">XL</SelectItem>
                                  <SelectItem value="2xl">2XL</SelectItem>
                                  <SelectItem value="3xl">3XL</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Fuente</Label>
                              <Select value={configVisual.fuente_titulo} onValueChange={(v) => handleConfigChange('fuente_titulo', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="sans">Sans-serif</SelectItem>
                                  <SelectItem value="serif">Serif</SelectItem>
                                  <SelectItem value="mono">Monospace</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Peso</Label>
                              <Select value={configVisual.peso_titulo} onValueChange={(v) => handleConfigChange('peso_titulo', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="normal">Normal</SelectItem>
                                  <SelectItem value="medium">Medio</SelectItem>
                                  <SelectItem value="semibold">Semi-negrita</SelectItem>
                                  <SelectItem value="bold">Negrita</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        {/* Sección: Descripción */}
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2 border-b pb-2">
                            <Type className="h-4 w-4" />
                            Descripción
                          </h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Color</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="color"
                                  value={configVisual.color_descripcion}
                                  onChange={(e) => handleConfigChange('color_descripcion', e.target.value)}
                                  className="w-12 h-10 p-1 cursor-pointer"
                                />
                                <Input
                                  value={configVisual.color_descripcion}
                                  onChange={(e) => handleConfigChange('color_descripcion', e.target.value)}
                                  className="flex-1"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Tamaño</Label>
                              <Select value={configVisual.tamaño_descripcion} onValueChange={(v) => handleConfigChange('tamaño_descripcion', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="sm">Pequeño</SelectItem>
                                  <SelectItem value="base">Normal</SelectItem>
                                  <SelectItem value="lg">Grande</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        {/* Sección: Botones */}
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2 border-b pb-2">
                            <Square className="h-4 w-4" />
                            Botón Principal
                          </h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Color de fondo</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="color"
                                  value={configVisual.color_boton_fondo}
                                  onChange={(e) => handleConfigChange('color_boton_fondo', e.target.value)}
                                  className="w-12 h-10 p-1 cursor-pointer"
                                />
                                <Input
                                  value={configVisual.color_boton_fondo}
                                  onChange={(e) => handleConfigChange('color_boton_fondo', e.target.value)}
                                  className="flex-1"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Color del texto</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="color"
                                  value={configVisual.color_boton_texto}
                                  onChange={(e) => handleConfigChange('color_boton_texto', e.target.value)}
                                  className="w-12 h-10 p-1 cursor-pointer"
                                />
                                <Input
                                  value={configVisual.color_boton_texto}
                                  onChange={(e) => handleConfigChange('color_boton_texto', e.target.value)}
                                  className="flex-1"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Color hover</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="color"
                                  value={configVisual.color_boton_hover}
                                  onChange={(e) => handleConfigChange('color_boton_hover', e.target.value)}
                                  className="w-12 h-10 p-1 cursor-pointer"
                                />
                                <Input
                                  value={configVisual.color_boton_hover}
                                  onChange={(e) => handleConfigChange('color_boton_hover', e.target.value)}
                                  className="flex-1"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Redondeo</Label>
                              <Select value={configVisual.boton_redondeo} onValueChange={(v) => handleConfigChange('boton_redondeo', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Sin redondeo</SelectItem>
                                  <SelectItem value="sm">Pequeño</SelectItem>
                                  <SelectItem value="md">Mediano</SelectItem>
                                  <SelectItem value="lg">Grande</SelectItem>
                                  <SelectItem value="full">Completo</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Tamaño</Label>
                              <Select value={configVisual.boton_tamaño} onValueChange={(v) => handleConfigChange('boton_tamaño', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="sm">Pequeño</SelectItem>
                                  <SelectItem value="md">Mediano</SelectItem>
                                  <SelectItem value="lg">Grande</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        {/* Sección: Espaciado */}
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2 border-b pb-2">
                            <Move className="h-4 w-4" />
                            Espaciado (px)
                          </h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Padding del contenido</Label>
                              <Input
                                type="number"
                                value={configVisual.padding_contenido}
                                onChange={(e) => handleConfigChange('padding_contenido', e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Espacio título-descripción</Label>
                              <Input
                                type="number"
                                value={configVisual.espacio_titulo_descripcion}
                                onChange={(e) => handleConfigChange('espacio_titulo_descripcion', e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Espacio descripción-botones</Label>
                              <Input
                                type="number"
                                value={configVisual.espacio_descripcion_botones}
                                onChange={(e) => handleConfigChange('espacio_descripcion_botones', e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Espacio entre botones</Label>
                              <Input
                                type="number"
                                value={configVisual.espacio_entre_botones}
                                onChange={(e) => handleConfigChange('espacio_entre_botones', e.target.value)}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Sección: Animación */}
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2 border-b pb-2">
                            <Sparkles className="h-4 w-4" />
                            Animación
                          </h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Animación de entrada</Label>
                              <Select value={configVisual.animacion_entrada} onValueChange={(v) => handleConfigChange('animacion_entrada', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Sin animación</SelectItem>
                                  <SelectItem value="fade">Desvanecer</SelectItem>
                                  <SelectItem value="slide">Deslizar</SelectItem>
                                  <SelectItem value="zoom">Zoom</SelectItem>
                                  <SelectItem value="bounce">Rebote</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Duración (ms)</Label>
                              <Input
                                type="number"
                                value={configVisual.duracion_animacion}
                                onChange={(e) => handleConfigChange('duracion_animacion', parseInt(e.target.value) || 200)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </ScrollArea>

                    {/* Columna derecha: Vista previa */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        Vista Previa
                      </h3>
                      <div 
                        className="relative rounded-lg overflow-hidden border-2 border-dashed"
                        style={{ backgroundColor: '#f1f5f9' }}
                      >
                        {/* Simulación del overlay */}
                        <div 
                          className="absolute inset-0"
                          style={{ 
                            backgroundColor: configVisual.color_overlay,
                            opacity: configVisual.opacidad_overlay / 100
                          }}
                        />
                        
                        {/* Modal preview */}
                        <div className="relative p-8 flex items-center justify-center min-h-[500px]">
                          <div 
                            className={`w-full max-w-md overflow-hidden shadow-${configVisual.modal_sombra}`}
                            style={{ 
                              backgroundColor: configVisual.color_fondo_modal,
                              borderRadius: configVisual.modal_redondeo === 'none' ? '0' : 
                                configVisual.modal_redondeo === 'sm' ? '0.125rem' :
                                configVisual.modal_redondeo === 'md' ? '0.375rem' :
                                configVisual.modal_redondeo === 'lg' ? '0.5rem' :
                                configVisual.modal_redondeo === 'xl' ? '0.75rem' : '1rem'
                            }}
                          >
                            {/* Imagen de ejemplo */}
                            <div 
                              className="relative"
                              style={{ 
                                height: `${configVisual.imagen_altura}px`,
                                borderRadius: `${configVisual.imagen_redondeo}px ${configVisual.imagen_redondeo}px 0 0`
                              }}
                            >
                              <img
                                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600"
                                alt="Preview"
                                className="w-full h-full"
                                style={{ 
                                  objectFit: configVisual.imagen_ajuste,
                                  borderRadius: `${configVisual.imagen_redondeo}px ${configVisual.imagen_redondeo}px 0 0`
                                }}
                              />
                              {configVisual.mostrar_degradado_imagen && (
                                <div 
                                  className="absolute inset-0"
                                  style={{ 
                                    background: `linear-gradient(to bottom, ${configVisual.color_degradado_inicio}, ${configVisual.color_degradado_fin})`,
                                    borderRadius: `${configVisual.imagen_redondeo}px ${configVisual.imagen_redondeo}px 0 0`
                                  }}
                                />
                              )}
                            </div>
                            
                            {/* Contenido */}
                            <div style={{ padding: `${configVisual.padding_contenido}px` }}>
                              <h3 
                                className={`font-${configVisual.peso_titulo} text-${configVisual.tamaño_titulo} font-${configVisual.fuente_titulo}`}
                                style={{ 
                                  color: configVisual.color_titulo,
                                  marginBottom: `${configVisual.espacio_titulo_descripcion}px`
                                }}
                              >
                                Título de ejemplo
                              </h3>
                              <p 
                                className={`text-${configVisual.tamaño_descripcion}`}
                                style={{ 
                                  color: configVisual.color_descripcion,
                                  marginBottom: `${configVisual.espacio_descripcion_botones}px`
                                }}
                              >
                                Esta es una descripción de ejemplo para la publicidad emergente.
                              </p>
                              <div 
                                className="flex flex-wrap"
                                style={{ gap: `${configVisual.espacio_entre_botones}px` }}
                              >
                                <button
                                  className={`px-4 py-2 text-sm transition-colors rounded-${configVisual.boton_redondeo}`}
                                  style={{ 
                                    backgroundColor: configVisual.color_boton_fondo,
                                    color: configVisual.color_boton_texto
                                  }}
                                >
                                  Botón Principal
                                </button>
                                <button
                                  className="px-4 py-2 text-sm"
                                  style={{ 
                                    color: configVisual.color_boton_cerrar_texto,
                                    backgroundColor: configVisual.color_boton_cerrar
                                  }}
                                >
                                  Cerrar
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Dialog para crear/editar */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editando ? "Editar Publicidad" : "Nueva Publicidad"}
              </DialogTitle>
              <DialogDescription>
                Configura los detalles de la publicidad que aparecerá a los usuarios
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Imagen */}
              <div className="space-y-2">
                <Label>Imagen de la publicidad</Label>
                <div className="flex items-start gap-4">
                  {formData.imagen_url ? (
                    <img
                      src={formData.imagen_url.startsWith('http') ? formData.imagen_url : `${API_URL}${formData.imagen_url}`}
                      alt="Preview"
                      className="w-32 h-24 object-contain rounded-lg border bg-slate-50"
                    />
                  ) : (
                    <div className="w-32 h-24 bg-slate-100 rounded-lg border-2 border-dashed flex items-center justify-center">
                      <Image className="h-8 w-8 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <Label htmlFor="imagen-upload" className="cursor-pointer">
                      <div
                        onDrop={handleDropImagen}
                        onDragOver={handleDragOverImagen}
                        onDragLeave={handleDragLeaveImagen}
                        data-testid="imagen-dropzone"
                        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                          isDraggingImagen
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'hover:border-blue-500'
                        }`}
                      >
                        <Upload className={`h-6 w-6 mx-auto mb-2 ${isDraggingImagen ? 'text-blue-500' : 'text-slate-400'}`} />
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          {uploading
                            ? "Subiendo..."
                            : isDraggingImagen
                              ? "Suelta la imagen aquí"
                              : "Arrastra una imagen o haz click para subir"}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          JPG, PNG, GIF o WebP (máx 5MB)
                        </p>
                      </div>
                      <input
                        id="imagen-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                        disabled={uploading}
                      />
                    </Label>
                    <div className="mt-2">
                      <Input
                        placeholder="O ingresa una URL de imagen"
                        value={formData.imagen_url}
                        onChange={(e) => handleInputChange('imagen_url', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Título y descripción */}
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label>Título (opcional)</Label>
                  <Input
                    placeholder="Ej: Bienvenido [nombre.usuario]"
                    value={formData.titulo}
                    onChange={(e) => handleInputChange('titulo', e.target.value)}
                    data-testid="publicidad-titulo-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descripción (opcional)</Label>
                  <Textarea
                    placeholder="Describe la promoción o mensaje..."
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    rows={3}
                    data-testid="publicidad-descripcion-input"
                  />
                </div>
              </div>

              {/* Variables dinámicas disponibles */}
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <Label className="text-amber-700 dark:text-amber-300 mb-2 block text-sm font-medium">
                  Variables dinámicas disponibles
                </Label>
                <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                  Usa estas variables en el título, descripción o texto del botón para personalizar el mensaje:
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { var: '[nombre.usuario]', desc: 'Nombre del usuario' },
                    { var: '[email.usuario]', desc: 'Email' },
                    { var: '[cargo.usuario]', desc: 'Cargo' },
                    { var: '[departamento.usuario]', desc: 'Departamento' },
                    { var: '[rol.usuario]', desc: 'Rol' },
                    { var: '[fecha.actual]', desc: 'Fecha actual' },
                    { var: '[hora.actual]', desc: 'Hora actual' },
                    { var: '[dia.semana]', desc: 'Día de la semana' },
                  ].map(({ var: variable, desc }) => (
                    <button
                      key={variable}
                      type="button"
                      onClick={() => {
                        // Copiar al portapapeles
                        navigator.clipboard.writeText(variable);
                        toast.success(`${variable} copiado al portapapeles`);
                      }}
                      className="px-2 py-1 bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-100 rounded text-xs font-mono hover:bg-amber-200 dark:hover:bg-amber-700 transition-colors"
                      title={desc}
                    >
                      {variable}
                    </button>
                  ))}
                </div>
              </div>

              {/* Botones de acción (hasta 3) */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Botones de Acción</Label>
                  {formData.botones.length < 3 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={agregarBoton}
                      className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Agregar Botón
                    </Button>
                  )}
                </div>
                
                {formData.botones.map((boton, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-slate-50 dark:bg-slate-800/50 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        Botón {index + 1}
                      </span>
                      {formData.botones.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => eliminarBoton(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Texto del botón</Label>
                        <Input
                          placeholder="Ej: Ver más"
                          value={boton.texto}
                          onChange={(e) => actualizarBoton(index, 'texto', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">URL del botón</Label>
                        <Input
                          placeholder="https://..."
                          value={boton.url}
                          onChange={(e) => actualizarBoton(index, 'url', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Puedes agregar hasta 3 botones. Cada botón puede tener un texto y URL diferente.
                </p>
              </div>

              {/* Frecuencia */}
              <div className="space-y-2">
                <Label>Frecuencia de aparición</Label>
                <Select
                  value={formData.frecuencia}
                  onValueChange={(value) => handleInputChange('frecuencia', value)}
                >
                  <SelectTrigger data-testid="publicidad-frecuencia-select">
                    <SelectValue placeholder="Selecciona frecuencia" />
                  </SelectTrigger>
                  <SelectContent>
                    {frecuenciaOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Recurrencia personalizada */}
              {formData.frecuencia === 'recurrente' && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <Label className="text-blue-700 dark:text-blue-300 mb-3 block">
                    Configurar recurrencia
                  </Label>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Mostrar cada</span>
                    <Input
                      type="number"
                      min="1"
                      value={formData.recurrencia_valor}
                      onChange={(e) => handleInputChange('recurrencia_valor', parseInt(e.target.value) || '')}
                      className="w-20"
                      placeholder="1"
                    />
                    <Select
                      value={formData.recurrencia_unidad}
                      onValueChange={(value) => handleInputChange('recurrencia_unidad', value)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Unidad" />
                      </SelectTrigger>
                      <SelectContent>
                        {recurrenciaUnidades.map(unidad => (
                          <SelectItem key={unidad.value} value={unidad.value}>
                            {unidad.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                    La publicidad se mostrará al usuario cada {formData.recurrencia_valor || '?'} {recurrenciaUnidades.find(u => u.value === formData.recurrencia_unidad)?.label.toLowerCase() || 'horas'}
                  </p>
                </div>
              )}

              {/* Notificación de escritorio (Web Push) */}
              <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <MonitorSmartphone className="h-5 w-5 text-indigo-600 dark:text-indigo-300 mt-0.5" />
                    <div>
                      <Label className="text-indigo-700 dark:text-indigo-300 font-semibold">
                        También enviar como notificación de escritorio
                      </Label>
                      <p className="text-xs text-indigo-600/80 dark:text-indigo-400 mt-1 max-w-md">
                        Aparece sobre todos los programas (aunque el navegador esté minimizado),
                        a la hora/recurrencia que configures arriba. Cada usuario debe permitir
                        las notificaciones en su navegador.
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.notificacion_escritorio}
                    onCheckedChange={(v) => handleInputChange('notificacion_escritorio', v)}
                    data-testid="publicidad-notif-escritorio-switch"
                  />
                </div>

                {formData.notificacion_escritorio && (
                  <div className="mt-4 space-y-4 border-t border-indigo-200 dark:border-indigo-800 pt-4">
                    {/* Destinatario */}
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600 dark:text-slate-400">
                        Destinatario
                      </Label>
                      <Select
                        value={formData.notificacion_target_user_id || 'TODOS'}
                        onValueChange={(v) => handleInputChange('notificacion_target_user_id', v === 'TODOS' ? '' : v)}
                      >
                        <SelectTrigger data-testid="publicidad-notif-target-select">
                          <SelectValue placeholder="Todos los usuarios" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="TODOS">Todos los usuarios suscritos</SelectItem>
                          {usuariosLista.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name || u.email} {u.email ? `(${u.email})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-slate-500">
                        Por defecto llega a todos. Elige un usuario específico solo para hacer pruebas de visualización.
                      </p>
                    </div>

                    {/* Días de la semana */}
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600 dark:text-slate-400">
                        Días de la semana (opcional)
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {diasSemanaOptions.map((d) => {
                          const selected = (formData.dias_semana || []).includes(d.value);
                          return (
                            <button
                              type="button"
                              key={d.value}
                              data-testid={`publicidad-dia-${d.value}`}
                              onClick={() => {
                                const actuales = formData.dias_semana || [];
                                const nuevos = selected
                                  ? actuales.filter((x) => x !== d.value)
                                  : [...actuales, d.value].sort((a, b) => a - b);
                                handleInputChange('dias_semana', nuevos);
                              }}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                selected
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600'
                              }`}
                            >
                              {d.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-slate-500">
                        Si no seleccionas ninguno, se enviará todos los días. Ej: para "apagar la computadora" elige Lun-Vie.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Targeting por Menú/Submenú */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <Label className="text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Segmentación por página (opcional)
                </Label>
                <p className="text-xs text-slate-500 mb-3">
                  Selecciona un menú específico donde mostrar esta publicidad. Si no seleccionas nada, se mostrará en todas las páginas.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Menú destino</Label>
                    <Select
                      value={formData.menu_destino || "all"}
                      onValueChange={(value) => {
                        handleInputChange('menu_destino', value === "all" ? '' : value);
                        handleInputChange('submenu_destino', ''); // Reset submenú al cambiar menú
                      }}
                    >
                      <SelectTrigger data-testid="publicidad-menu-select">
                        <SelectValue placeholder="Todas las páginas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las páginas</SelectItem>
                        {menusDisponibles.map(menu => (
                          <SelectItem key={menu.id} value={menu.id}>
                            {menu.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Submenú destino</Label>
                    <Select
                      value={formData.submenu_destino || "all"}
                      onValueChange={(value) => handleInputChange('submenu_destino', value === "all" ? '' : value)}
                      disabled={!formData.menu_destino}
                    >
                      <SelectTrigger data-testid="publicidad-submenu-select">
                        <SelectValue placeholder={formData.menu_destino ? "Todo el menú" : "Selecciona un menú primero"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todo el menú</SelectItem>
                        {getSubmenusDelMenu().map(submenu => (
                          <SelectItem key={submenu.id} value={submenu.id}>
                            {submenu.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Fechas */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha de inicio (opcional)</Label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio}
                    onChange={(e) => handleInputChange('fecha_inicio', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha de fin (opcional)</Label>
                  <Input
                    type="date"
                    value={formData.fecha_fin}
                    onChange={(e) => handleInputChange('fecha_fin', e.target.value)}
                  />
                </div>
              </div>

              {/* Horas */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hora de inicio (opcional)</Label>
                  <Input
                    type="time"
                    value={formData.hora_inicio}
                    onChange={(e) => handleInputChange('hora_inicio', e.target.value)}
                  />
                  <p className="text-xs text-slate-500">
                    Mostrar desde esta hora
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Hora de fin (opcional)</Label>
                  <Input
                    type="time"
                    value={formData.hora_fin}
                    onChange={(e) => handleInputChange('hora_fin', e.target.value)}
                  />
                  <p className="text-xs text-slate-500">
                    Mostrar hasta esta hora
                  </p>
                </div>
              </div>

              {/* Orden y estado */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Orden de prioridad</Label>
                  <Input
                    type="number"
                    value={formData.orden}
                    onChange={(e) => handleInputChange('orden', parseInt(e.target.value) || 0)}
                    min={0}
                  />
                  <p className="text-xs text-slate-500">
                    Menor número = mayor prioridad
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <div className="flex items-center gap-3 pt-2">
                    <Switch
                      checked={formData.activo}
                      onCheckedChange={(checked) => handleInputChange('activo', checked)}
                    />
                    <span className="text-sm text-slate-600">
                      {formData.activo ? "Activa" : "Inactiva"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} data-testid="guardar-publicidad-btn">
                {editando ? "Guardar cambios" : "Crear publicidad"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog "Enviar Prueba" de notificación de escritorio */}
        <Dialog open={pruebaPush.open} onOpenChange={(o) => !o && setPruebaPush({ open: false, pub: null, selectedUserId: '', sending: false })}>
          <DialogContent className="sm:max-w-md" data-testid="enviar-prueba-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-indigo-600" />
                Enviar prueba de notificación
              </DialogTitle>
              <DialogDescription>
                Selecciona el usuario al que se le enviará ahora mismo la notificación de escritorio
                {pruebaPush.pub ? <> de <strong>“{pruebaPush.pub.titulo || 'Publicidad'}”</strong></> : null}.
                El usuario debe tener las notificaciones activadas en su navegador.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <Input
                placeholder="Buscar usuario por nombre o correo…"
                value={pruebaSearch}
                onChange={(e) => setPruebaSearch(e.target.value)}
                data-testid="enviar-prueba-search"
                autoFocus
              />
              <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 divide-y dark:divide-slate-700">
                {usuariosLista
                  .filter((u) => {
                    const q = pruebaSearch.trim().toLowerCase();
                    if (!q) return true;
                    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                  })
                  .slice(0, 100)
                  .map((u) => {
                    const selected = pruebaPush.selectedUserId === u.id;
                    return (
                      <button
                        type="button"
                        key={u.id}
                        data-testid={`enviar-prueba-user-${u.id}`}
                        onClick={() => setPruebaPush((prev) => ({ ...prev, selectedUserId: u.id }))}
                        className={`w-full text-left px-3 py-2 transition-colors ${
                          selected
                            ? 'bg-indigo-50 dark:bg-indigo-900/30'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        <p className={`text-sm font-medium ${selected ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-700 dark:text-slate-200'}`}>
                          {u.name || u.email}
                        </p>
                        {u.email && <p className="text-xs text-slate-400">{u.email}</p>}
                      </button>
                    );
                  })}
                {usuariosLista.filter((u) => {
                  const q = pruebaSearch.trim().toLowerCase();
                  if (!q) return true;
                  return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                }).length === 0 && (
                  <p className="px-3 py-4 text-center text-sm text-slate-400">Sin resultados</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setPruebaPush({ open: false, pub: null, selectedUserId: '', sending: false })}>
                Cancelar
              </Button>
              <Button
                onClick={enviarPruebaPush}
                disabled={!pruebaPush.selectedUserId || pruebaPush.sending}
                data-testid="enviar-prueba-confirm-btn"
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Send className="h-4 w-4 mr-2" />
                {pruebaPush.sending ? "Enviando…" : "Enviar prueba"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de vista previa */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="sm:max-w-lg md:max-w-xl p-0 overflow-hidden">
            {previewData && (
              <div className="relative bg-white rounded-xl overflow-hidden">
                {previewData.imagen_url && (
                  <img
                    src={previewData.imagen_url.startsWith('http') ? previewData.imagen_url : `${API_URL}${previewData.imagen_url}`}
                    alt={previewData.titulo || "Preview"}
                    className="w-full h-auto max-h-[300px] object-cover"
                  />
                )}
                <div className="p-6">
                  {previewData.titulo && (
                    <h2 className="text-2xl font-bold text-slate-800 mb-3">
                      {previewData.titulo}
                    </h2>
                  )}
                  {previewData.descripcion && (
                    <p className="text-slate-600 mb-5">
                      {previewData.descripcion}
                    </p>
                  )}
                  <div className="flex gap-3 justify-end">
                    <Button variant="ghost" onClick={() => setPreviewOpen(false)}>
                      Cerrar
                    </Button>
                    {previewData.boton_texto && (
                      <Button className="bg-gradient-to-r from-blue-600 to-indigo-600">
                        {previewData.boton_texto}
                        {previewData.boton_url && <ExternalLink className="ml-2 h-4 w-4" />}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
